import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Film, ExternalLink, Play, Download, Send, Loader2, CheckCircle2, X } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VideoResources({ onBack }) {
  const [resources, setResources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Send To modal state
  const [sendingTo, setSendingTo] = useState(null); // resource object or null
  const [recipientFirst, setRecipientFirst] = useState("");
  const [recipientLast, setRecipientLast] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [sendSuccess, setSendSuccess] = useState("");
  const [sendError, setSendError] = useState("");

  const getAuthHeaders = () => {
    const token = localStorage.getItem("token");
    return { Authorization: `Bearer ${token}` };
  };

  useEffect(() => {
    const fetchResources = async () => {
      try {
        const res = await axios.get(`${API}/resources/published`, { headers: getAuthHeaders() });
        setResources(res.data || []);
      } catch (err) {
        console.error("Failed to fetch resources:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchResources();
  }, []);

  const openSendModal = (resource) => {
    setSendingTo(resource);
    setRecipientFirst("");
    setRecipientLast("");
    setRecipientEmail("");
    setSendSuccess("");
    setSendError("");
  };

  const closeSendModal = () => {
    setSendingTo(null);
    setSendSuccess("");
    setSendError("");
  };

  const handleSend = async () => {
    if (!recipientFirst.trim() || !recipientLast.trim() || !recipientEmail.trim()) {
      setSendError("All fields are required.");
      return;
    }
    setSendError("");
    setSendLoading(true);
    try {
      await axios.post(`${API}/resources/${sendingTo.id}/send`, {
        recipient_first_name: recipientFirst.trim(),
        recipient_last_name: recipientLast.trim(),
        recipient_email: recipientEmail.trim(),
      }, { headers: getAuthHeaders() });
      setSendSuccess(`Video link sent to ${recipientEmail.trim()}`);
      setRecipientFirst("");
      setRecipientLast("");
      setRecipientEmail("");
    } catch (err) {
      setSendError(err.response?.data?.detail || "Failed to send. Please try again.");
    } finally {
      setSendLoading(false);
    }
  };

  const categories = ["All", ...new Set(resources.map((r) => r.category).filter(Boolean))];
  const filtered = selectedCategory === "All" ? resources : resources.filter((r) => r.category === selectedCategory);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="sm" onClick={onBack} className="text-slate-600 hover:text-slate-900" data-testid="video-resources-back-btn">
            <ArrowLeft className="h-4 w-4 mr-1" /> Back to Dashboard
          </Button>
        </div>

        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900" data-testid="video-resources-title">Video Resources</h1>
          <p className="text-sm text-slate-500 mt-1">Browse product videos and share links with your customers.</p>
        </div>

        {/* Category Filter */}
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((cat) => (
              <Button key={cat} type="button" variant={selectedCategory === cat ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(cat)} className="text-sm" data-testid={`filter-${cat}`}>
                {cat}
              </Button>
            ))}
          </div>
        )}

        {loading ? (
          <p className="text-slate-500">Loading...</p>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-slate-200">
            <Film className="h-10 w-10 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500 font-medium">No video resources available yet.</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {filtered.map((r) => (
              <div key={r.id} className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid={`video-card-${r.id}`}>
                {/* Video Preview */}
                <a href={`/resource/${r.id}`} target="_blank" rel="noopener noreferrer" className="block relative bg-slate-900 aspect-video group cursor-pointer">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-white/20 backdrop-blur-sm rounded-full p-4 group-hover:bg-white/30 transition-colors">
                      <Play className="h-8 w-8 text-white fill-white" />
                    </div>
                  </div>
                  <div className="absolute bottom-2 left-3">
                    <span className="bg-black/60 text-white text-[10px] font-semibold px-2 py-0.5 rounded">{r.category}</span>
                  </div>
                </a>

                {/* Info */}
                <div className="p-4">
                  <h3 className="text-sm font-semibold text-slate-900 mb-1">{r.title}</h3>
                  {r.description && <p className="text-xs text-slate-500 mb-3">{r.description}</p>}
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" className="text-xs h-7 flex-1" onClick={() => openSendModal(r)} data-testid={`send-to-${r.id}`}>
                      <Send className="h-3 w-3 mr-1" /> Send To
                    </Button>
                    <a href={`/resource/${r.id}`} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="text-xs h-7">
                        <ExternalLink className="h-3 w-3 mr-1" /> Watch
                      </Button>
                    </a>
                    <a href={`${API}/resource/${r.id}/download`} download>
                      <Button variant="outline" size="sm" className="text-xs h-7">
                        <Download className="h-3 w-3 mr-1" /> Download
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Send To Modal */}
      {sendingTo && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]" data-testid="send-to-modal-overlay">
          <div className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md mx-4" data-testid="send-to-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Send className="h-5 w-5 text-blue-600" />
                <h3 className="text-lg font-bold text-slate-900">Send Video Link</h3>
              </div>
              <button onClick={closeSendModal} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              Send <strong>{sendingTo.title}</strong> to a recipient.
            </p>

            <div className="space-y-3">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Recipient Information</p>
              <div>
                <Label className="text-sm text-slate-600">First Name *</Label>
                <Input
                  value={recipientFirst}
                  onChange={(e) => setRecipientFirst(e.target.value)}
                  placeholder="First name"
                  className="mt-1"
                  data-testid="send-first-name"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Last Name *</Label>
                <Input
                  value={recipientLast}
                  onChange={(e) => setRecipientLast(e.target.value)}
                  placeholder="Last name"
                  className="mt-1"
                  data-testid="send-last-name"
                />
              </div>
              <div>
                <Label className="text-sm text-slate-600">Email Address *</Label>
                <Input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  placeholder="email@example.com"
                  className="mt-1"
                  data-testid="send-email"
                />
              </div>
            </div>

            {sendError && <p className="text-sm text-red-600 mt-3" data-testid="send-error">{sendError}</p>}
            {sendSuccess && (
              <p className="text-sm text-green-600 mt-3 flex items-center gap-1" data-testid="send-success">
                <CheckCircle2 className="h-4 w-4" /> {sendSuccess}
              </p>
            )}

            <div className="flex gap-3 mt-5">
              <Button variant="outline" className="flex-1 h-10" onClick={closeSendModal}>
                Cancel
              </Button>
              <Button
                className="flex-1 h-10 bg-blue-600 hover:bg-blue-700"
                onClick={handleSend}
                disabled={sendLoading}
                data-testid="send-submit-btn"
              >
                {sendLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                Send
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
